import {StyleSheet, css} from 'aphrodite';
import React from 'react';

import {PrimaryExperimentalRoleType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {
  PRIMARY_ROLE_TO_COLOR,
  PRIMARY_ROLE_TO_DISPLAY_TEXT,
} from 'samples/experimental-roles/ExperimentalRoleConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import {NEAR_BLACK, WHITE} from 'ui-library/Colors';

const labelProps = {dominantBaseline: 'central', textAnchor: 'middle', cx: 0, cy: 0};

interface PrimaryExperimentalRoleSymbolProps {
  radius: number;
  primaryRole: PrimaryExperimentalRoleType;
  label: string | null;
  // Optionally define center position of the symbol
  cx?: number;
  cy?: number;
}

/**
 * Displays an SVG symbol representing a primary role.
 */
export default function PrimaryExperimentalRoleSymbol({
  radius,
  primaryRole,
  label,
  cx,
  cy,
}: PrimaryExperimentalRoleSymbolProps): JSX.Element {
  const svgSize = radius * 2;
  const x = cx ?? radius;
  const y = cy ?? radius;
  const labelSize = Math.min((label?.length ?? 0) * 10, svgSize);
  const ariaLabel = `${PRIMARY_ROLE_TO_DISPLAY_TEXT[primaryRole].displayName} role symbol`;

  return (
    <svg
      aria-label={ariaLabel}
      className={css(styles.symbolSvg)}
      role="img"
      width={svgSize}
      height={svgSize}
      {...getTestProps({component: 'PrimaryExperimentalRoleSymbol', element: 'root', key: primaryRole})}
      data-amp-safe={false}
    >
      <g transform={`translate(${x},${y})`}>
        {primaryRole === PrimaryExperimentalRoleType.BLANK ? (
          <>
            <circle
              cx={0}
              cy={0}
              r={radius}
              fill={WHITE}
              stroke={NEAR_BLACK}
              strokeWidth={1}
              {...getTestProps({
                component: 'PrimaryExperimentalRoleSymbol',
                element: 'blankCircle',
                key: primaryRole,
              })}
              data-amp-safe={false}
            />
            <line
              transform="rotate(45)"
              x1={0}
              y1={-radius}
              x2={0}
              y2={radius}
              stroke={NEAR_BLACK}
              strokeWidth={1}
              {...getTestProps({
                component: 'PrimaryExperimentalRoleSymbol',
                element: 'blankDiagonalLine',
                key: primaryRole,
              })}
              data-amp-safe={false}
            />
            {label && (
              <>
                {/* Draw the label with a white background so the line doesn't interfere with the text */}
                <rect x={-labelSize / 2} y={-7} width={labelSize} height={14} fill={WHITE} />
                <text className={css(styles.textLabel)} fill={NEAR_BLACK} {...labelProps}>
                  {label}
                </text>
              </>
            )}
          </>
        ) : (
          <>
            <circle
              cx={0}
              cy={0}
              r={radius}
              fill={PRIMARY_ROLE_TO_COLOR[primaryRole]}
              {...getTestProps({
                component: 'PrimaryExperimentalRoleSymbol',
                element: 'roleCircle',
                key: primaryRole,
              })}
              data-amp-safe={false}
            />
            {label && (
              <text className={css(styles.textLabel)} fill={NEAR_BLACK} {...labelProps}>
                {label}
              </text>
            )}
          </>
        )}
      </g>
    </svg>
  );
}

const styles = StyleSheet.create({
  symbolSvg: {overflow: 'visible'},
  textLabel: {cursor: 'inherit'},
});
